import { AxiosInstance, AxiosResponse } from 'axios';
import { axiosInstance } from '../axios';
import { VALUATION_PRICE } from './financial-report.service';

export type ValuationStatus =
    | 'Pending'
    | 'Running'
    | 'Completed'
    | 'Failed'
    | 'Refunded';

export interface PurchaseValuationResponse {
    valuationId: number;
    status: ValuationStatus;
    creditsSpent: number;
    remainingUserCredits: number;
}

export interface ValuationMethodPayload {
    applicable?: boolean;
    multiple?: number | null;
    baseValue?: number | null;
    adjustmentPct?: number | null;
    adjustedValue?: number | null;
    sizeDiscountPct?: number | null;
    finalEnterpriseValue?: number | null;
    equity?: number | null;
    reason?: string;
}

export interface ValuationFinancialsPayload {
    revenue?: number | null;
    revenueSource?: string | null;
    ebitda?: number | null;
    ebitdaSource?: string | null;
    netIncome?: number | null;
    netIncomeSource?: string | null;
    totalAssets?: number | null;
    totalAssetsSource?: string | null;
    totalLiabilities?: number | null;
    totalLiabilitiesSource?: string | null;
    cash?: number | null;
    cashSource?: string | null;
    totalDebt?: number | null;
    totalDebtSource?: string | null;
}

export interface ValuationPayload {
    status: string;
    companyName?: string | null;
    registrationCountry?: string | null;
    industry?: string | null;
    aiSummary?: string | null;
    financials?: ValuationFinancialsPayload;
    evEbitda?: ValuationMethodPayload;
    pe?: ValuationMethodPayload;
    netAssetValue?: number | null;
    warnings?: string[];
    error?: string;
}

export interface Valuation {
    id: number;
    companyId: number;
    status: ValuationStatus;
    creditsSpent: number;
    createdAt: number;
    completedAt: number | null;
    dataJson: string | null;
    errorMessage: string | null;
    externalJobId: string | null;
    progressPct?: number | null;
    progressStep?: string | null;
    progressMessage?: string | null;
}

class ValuationService {
    private axios: AxiosInstance;

    constructor() {
        this.axios = axiosInstance;
    }

    async purchaseValuation(companyId: number): Promise<PurchaseValuationResponse> {
        const response = await this.axios.post(
            `/api/companies/${companyId}/valuations`,
            { creditCountToSpend: VALUATION_PRICE },
        );
        return response.data;
    }

    async getValuation(valuationId: number): Promise<AxiosResponse<Valuation>> {
        return await this.axios.get(`/api/valuations/${valuationId}`).catch((err) => err.response);
    }

    parseValuationPayload(valuation: Valuation): ValuationPayload | null {
        if (!valuation.dataJson) return null;
        try {
            return JSON.parse(valuation.dataJson) as ValuationPayload;
        } catch {
            return null;
        }
    }
}

export const valuationService = new ValuationService();
